import React from 'react';
import { HeadingTag, Button, ModuleBase, Media, Stack, Text, Box, ImageCarousel } from '@/components';
import classNames from 'classnames';
import { isColorDark, fixColor } from '@/utils';

const TextAndTwoMediaModule = ({ data }) => {
  const darkBg = isColorDark(fixColor(data.backgroundColour));
  const isLandscape = data.mediaOrientation?.toLowerCase() === 'landscape';

  // TACTICAL: landscape instances skip the carousel — side by side from sm up,
  // stacked on phones. Portrait keeps the original carousel behaviour untouched.
  // Backlogged for a proper CMS-driven layout option independent of orientation.
  const sideBySide = isLandscape;

  // Desktop image aspect + Cloudflare crop dimensions per orientation
  const desktopAspect = isLandscape ? 'aspect-[4/3]' : 'aspect-[432/552]';
  const imgWidths = isLandscape ? { xs: 800 } : { xs: 432 };
  const imgHeights = isLandscape ? { xs: 600 } : { xs: 552 };

  // Mobile aspect + dimensions per orientation
  const mobileWrapperAspect = isLandscape ? 'aspect-[4/3]' : 'aspect-[345/416] sm:aspect-[365/440]';
  const mobileWidths = isLandscape ? { xs: 460, sm: 460, md: 620 } : { xs: 365, sm: 332, md: 460 };
  const mobileHeights = isLandscape ? { xs: 345, sm: 345, md: 465 } : { xs: 440, sm: 400, md: 555 };

  // One shape for both slots: the video model when present, otherwise the
  // legacy image wrapped so <Media> can render it. The builder has already
  // populated coverImage from the legacy image, so captions work either way.
  const leftSlot = data.leftVideo || (data.leftMedia ? { coverImage: data.leftMedia } : null);
  const rightSlot = data.rightVideo || (data.rightMedia ? { coverImage: data.rightMedia } : null);
  const slots = [leftSlot, rightSlot].filter(Boolean);

  const hasHeading = (data.headingTitle && data.headingTitle.heading) || data.preHeading;

  const heading = hasHeading && (
    <div className="flex flex-col gap-3 lg:items-center">
      {data.preHeading && (
        <Text
          className={classNames(
            'font-gotham text-sm font-n-light leading-6 lg:mb-3',
            darkBg ? 'text-white/75' : 'text-black/75',
          )}
        >
          {data.preHeading}
        </Text>
      )}
      {data.headingTitle && data.headingTitle.heading && (
        <HeadingTag
          animate
          data={data.headingTitle}
          className={classNames(
            'font-larken text-desktop-small font-light not-italic leading-mobile-medium sm:text-desktop-medium sm:leading-10 lg:w-[328px] lg:text-[38px]',
            darkBg ? 'text-white' : 'text-black',
          )}
        />
      )}
    </div>
  );

  const descriptionAndCTA = (data.description || data.primaryCTA) && (
    <>
      {data.description && (
        <Box
          className={classNames(
            'font-gotham text-sm font-s-light leading-6 opacity-90 lg:w-2/3',
            darkBg ? 'text-white/75' : 'text-black/75',
          )}
          dangerouslySetInnerHTML={{ __html: data.description }}
        />
      )}
      {data.primaryCTA && (
        <Button
          animate
          link={data.primaryCTA}
          className={classNames('btn secondary w-full sm:w-auto sm:!px-11 lg:!px-5', !darkBg && 'dark')}
        />
      )}
    </>
  );

  const captionClasses = (alignRight) =>
    classNames(
      'captions mt-2.5 line-clamp-2 font-gotham text-small-paragraph font-n-light not-italic leading-desktop-small',
      alignRight && 'text-right',
      darkBg ? 'text-white' : 'text-black',
    );

  const mediaSlot = (slot, { aspect, widths, heights, alignRight = false, key } = {}) =>
    slot && (
      <div key={key} className="flex w-full flex-col">
        <div className={classNames('relative w-full overflow-hidden', aspect)}>
          <Media animate media={slot} widths={widths} heights={heights} cover background={slot.autoPlay} />
        </div>
        <Text className={captionClasses(alignRight)}>{slot.coverImage?.caption}</Text>
      </div>
    );

  // Phones (landscape only): stack both slots instead of a carousel.
  const mobileStack = (
    <div className="flex w-full flex-col gap-6">
      {slots.map((slot, index) =>
        mediaSlot(slot, { aspect: mobileWrapperAspect, widths: mobileWidths, heights: mobileHeights, key: index }),
      )}
    </div>
  );

  const mobileMedia = (
    <ImageCarousel
      loop
      asMedia
      images={slots}
      portrait={false}
      imageWidths={mobileWidths}
      imageHeights={mobileHeights}
      wrapperClass={mobileWrapperAspect}
      wrapperClass2="w-full"
      isDarkBg={darkBg}
    />
  );

  return (
    <ModuleBase data={{ ...data }}>
      <div
        className={classNames(
          'container flex flex-col items-center gap-8 px-6 md:gap-10 lg:gap-16',
          sideBySide ? 'sm:px-12 md:px-24' : 'sm:px-24',
          isLandscape ? 'xl:px-[120px]' : 'xl:px-[272px]',
        )}
      >
        {heading && (
          <Stack
            className={classNames('text-wrapper w-full lg:text-center', sideBySide ? 'block' : 'sm:hidden lg:block')}
          >
            {heading}
          </Stack>
        )}

        <Stack className={classNames('w-full flex-col gap-y-[17px]', sideBySide ? 'hidden sm:flex' : 'hidden lg:flex')}>
          <div className="flex w-full gap-x-4 lg:gap-x-8">
            {mediaSlot(leftSlot, { aspect: desktopAspect, widths: imgWidths, heights: imgHeights })}
            {mediaSlot(rightSlot, { aspect: desktopAspect, widths: imgWidths, heights: imgHeights, alignRight: true })}
          </div>
        </Stack>

        <Stack className="flex sm:hidden">{sideBySide ? mobileStack : mobileMedia}</Stack>

        <Stack
          className={classNames(
            'flex w-full flex-col gap-8 sm:flex-row sm:justify-between sm:gap-0',
            sideBySide ? '' : 'sm:hidden lg:flex',
          )}
        >
          {descriptionAndCTA}
        </Stack>
      </div>

      <Stack className={classNames('container gap-6 lg:hidden', sideBySide ? 'hidden' : 'hidden sm:flex')}>
        <Box className="w-1/2">{mobileMedia}</Box>
        <Box className="flex w-1/2 flex-col justify-center gap-6 pr-6">
          {heading}
          {descriptionAndCTA}
        </Box>
      </Stack>
    </ModuleBase>
  );
};

export default TextAndTwoMediaModule;
